/**
 * Interview Questions Prompts
 * 
 * Functions for generating strategic interview questions for podcast episodes.
 * Uses canonical field definitions via getFullName helper.
 */

import aiService from '../../services/aiService.js';
import {
    COMMON_GUARDRAILS,
    COT_INSTRUCTIONS,
    buildStructuredPrompt
} from '../../utils/promptTemplates.js';

/**
 * Generate Interview Questions
 * Creates deep and strategic interview questions for podcast guests
 * 
 * @param {object} context - { guestName, guestBio, topics, tone, quantity }
 * @returns {Promise<string>} - Formatted markdown list of questions
 */
export const generateInterviewQuestions = async (context) => {
    const tone = context.tone || 'Strategic and Thought-Provoking';
    const quantity = context.quantity || '7-10';

    const sections = {
        role: "You are a world-class Podcast Host (like Tim Ferriss or Lex Fridman). Your goal is to ask questions that reveal deep insights, not surface-level PR answers.",

        context: `
**GUEST:** ${context.guestName || 'Unknown'}
**BIO/DOSSIER:**
${context.guestBio || 'No bio provided.'}

**AGREED TOPICS:**
${context.topics || 'General life and career'}
        `,

        task: `
Generate ${quantity} interview questions.
The tone should be **${tone}**.
Questions should range from "Icebreakers" to "Deep Dives".
        `,

        instructions: `
${COT_INSTRUCTIONS}

1. **Analyze the Bio:** Find the "unusual" facts.
2. **Avoid Clichés:** Do NOT ask "How did you get started?" unless it's a specific angle.
3. **Structure:**
    - **Warm Up:** 2 questions to build rapport.
    - **The Deep Dive:** Most questions exploring the core topics.
    - **Closing:** 1-2 rapid fire or reflective questions.
4. **Tone Enforcement:** Ensure every question reflects the requested tone: "${tone}".
        `,

        format: `
**OUTPUT FORMAT:**

### 🧊 Warm Up
1. **[Question]**
> *Why ask:* [Brief rationale]

### 🌊 Deep Dive (${tone})
2. **[Question]**
> *Context:* [Mention specific fact from bio]

...and so on.
        `,

        guardrails: COMMON_GUARDRAILS
    };

    const prompt = buildStructuredPrompt(sections);
    return await aiService.generateContent(prompt, 'PLANNING');
};

const interviewQuestionsExports = {
    generateInterviewQuestions
};

export default interviewQuestionsExports;
